<template>
  <div
    class="job-schedule-editor"
    :class="{ 'job-schedule-editor--compact': compact }"
    @focusin="$emit('focus')"
    @focusout="focusOut"
  >
    <label class="job-schedule-editor__date">
      <span v-if="!compact">Date</span>
      <FormInput
        ref="date"
        v-model="dateInput"
        :size="compact ? 'small' : 'large'"
        :disabled="disabled"
        :icon-right="compact ? '' : 'iconoir-calendar'"
        placeholder="MM/DD/YYYY"
        @focus="openContext('dateContext', $event)"
      />
      <Context
        ref="dateContext"
        :hide-on-click-outside="false"
        class="datepicker-context"
      >
        <ClientOnly>
          <DatePicker
            :inline="true"
            :monday-first="true"
            :use-utc="true"
            :model-value="pickerDate"
            :language="datePickerLanguage"
            :open-date="pickerDate || new Date()"
            class="datepicker"
            @update:model-value="chooseDate"
          />
        </ClientOnly>
      </Context>
    </label>

    <label>
      <span v-if="!compact">Start</span>
      <FormInput
        ref="startTime"
        v-model="startTimeInput"
        :size="compact ? 'small' : 'large'"
        :disabled="disabled"
        :icon-right="compact ? '' : 'iconoir-clock'"
        placeholder="Start"
        @focus="openContext('startContext', $event)"
      />
      <TimeSelectContext
        ref="startContext"
        :value="startTimeInput"
        :hide-on-click-outside="false"
        notation="12"
        @input="chooseTime('startTimeInput', $event)"
      />
    </label>

    <label>
      <span v-if="!compact">End</span>
      <FormInput
        ref="endTime"
        v-model="endTimeInput"
        :size="compact ? 'small' : 'large'"
        :disabled="disabled"
        :icon-right="compact ? '' : 'iconoir-clock'"
        placeholder="End"
        @focus="openContext('endContext', $event)"
      />
      <TimeSelectContext
        ref="endContext"
        :value="endTimeInput"
        :hide-on-click-outside="false"
        notation="12"
        @input="chooseTime('endTimeInput', $event)"
      />
    </label>
  </div>
</template>

<script>
import moment from '@baserow/modules/core/moment'
import TimeSelectContext from '@baserow/modules/core/components/TimeSelectContext'
import { useDatePickerLanguage } from '@baserow/modules/core/composables/useDatePickerLanguage'
import { isElement } from '@baserow/modules/core/utils/dom'
import { scheduleFromParts, scheduleParts } from '@peninsula/jobSchedule'

export default {
  name: 'JobScheduleEditor',
  components: { TimeSelectContext },
  props: {
    modelValue: { type: Object, default: null },
    timezone: { type: String, required: true },
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'focus', 'blur', 'prevent-unselect'],
  setup() {
    return useDatePickerLanguage()
  },
  data() {
    return { parts: scheduleParts(this.modelValue, this.timezone) }
  },
  computed: {
    dateInput: {
      get() {
        const date = moment.utc(this.parts.date, 'YYYY-MM-DD', true)
        return date.isValid() ? date.format('MM/DD/YYYY') : this.parts.date
      },
      set(value) {
        const date = moment.utc(value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true)
        this.parts.date = date.isValid() ? date.format('YYYY-MM-DD') : value
        this.update()
      },
    },
    startTimeInput: {
      get() {
        return this.formatTime(this.parts.startTime)
      },
      set(value) {
        this.setTime('startTime', value)
      },
    },
    endTimeInput: {
      get() {
        return this.formatTime(this.parts.endTime)
      },
      set(value) {
        this.setTime('endTime', value)
      },
    },
    pickerDate() {
      const date = moment.utc(this.parts.date, 'YYYY-MM-DD', true)
      return date.isValid() ? date.toDate() : null
    },
  },
  watch: {
    modelValue(value) {
      const next = scheduleParts(value, this.timezone)
      if (JSON.stringify(next) !== JSON.stringify(this.parts)) this.parts = next
    },
  },
  methods: {
    focus() {
      this.$refs.date.focus()
    },
    update() {
      this.$emit(
        'update:modelValue',
        scheduleFromParts(this.parts, this.timezone)
      )
    },
    chooseDate(value) {
      this.$emit('prevent-unselect')
      this.parts.date = moment(value).format('YYYY-MM-DD')
      this.update()
      this.$refs.dateContext.hide()
      this.$nextTick(() => this.$refs.startTime.focus())
    },
    chooseTime(part, value) {
      const start = part === 'startTimeInput'
      if (start) this.$emit('prevent-unselect')
      this[part] = value
      this.$refs[start ? 'startContext' : 'endContext'].hide()
      if (start) this.$nextTick(() => this.$refs.endTime.focus())
      else this.$nextTick(() => this.$emit('blur'))
    },
    formatTime(value) {
      const time = moment.utc(value, ['HH:mm', 'h:mm A', 'hh:mm A'], true)
      return time.isValid() ? time.format('h:mm A') : value
    },
    setTime(part, value) {
      const time = moment.utc(value, ['HH:mm', 'h:mm A', 'hh:mm A'], true)
      this.parts[part] = time.isValid() ? time.format('HH:mm') : value
      this.update()
    },
    openContext(ref, event) {
      for (const name of ['dateContext', 'startContext', 'endContext']) {
        if (name !== ref) this.$refs[name]?.hide()
      }
      this.$refs[ref].toggle(event.currentTarget, 'bottom', 'left', 0)
    },
    hasOpenContext() {
      return ['dateContext', 'startContext', 'endContext'].some((name) => {
        const component = this.$refs[name]
        const context = component?.getRootContext?.() || component
        return context?.isOpen?.() || false
      })
    },
    containsContextTarget(target) {
      return ['dateContext', 'startContext', 'endContext'].some(
        (name) => this.$refs[name] && isElement(this.$refs[name].$el, target)
      )
    },
    focusOut(event) {
      if (!this.$el.contains(event.relatedTarget) && !this.hasOpenContext()) {
        this.$emit('blur')
      }
    },
  },
}
</script>

<style scoped>
.job-schedule-editor {
  display: flex;
  gap: 12px;
}

.job-schedule-editor label {
  display: grid;
  flex: 1;
  gap: 6px;
  min-width: 0;
}

.job-schedule-editor label > span {
  font-size: 12px;
  font-weight: 600;
}

.job-schedule-editor__date {
  flex: 1.25;
}

.job-schedule-editor--compact {
  width: 100%;
  gap: 2px;
}

.job-schedule-editor--compact label {
  display: block;
}

.job-schedule-editor--compact :deep(.form-input__input) {
  padding: 0 6px;
  font-size: 11px;
}
</style>
